import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { BaseService, ConfigService, LoggingService, UtilsJson, UtilsMisc, UtilsString, retryWithDelay, ErrorTimeout, ErrorHttp, ErrorUnknown, ErrorNoNetwork } from '@pit/core';
import { IAuthenticationDataService } from '../interfaces/authentication.data.service.interface';
import { DtoUser, IDtoUser } from '@pit/frontend-model';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationDataService extends BaseService implements IAuthenticationDataService {

  constructor(private http: HttpClient, configService: ConfigService, loggingService: LoggingService) {
    super(configService, loggingService);
  }

  public authenticateUserName$(mandator: string, userName: string): Observable<any> {
    const body: any = {
      authenticationUserName: this.configurationService.configAuth.authUserName,
      authenticationPassword: this.configurationService.configAuth.authPassword,
      mandator: UtilsString.encodeBase64(mandator),
      userName: UtilsString.encodeBase64(userName)
    }
    return this.httpPost('/api/web/authenticate/username', body, 3000, 0).pipe(
      map((json: any) => {
        this.configurationService.configAuth.userName = json.userName;
        return this.configurationService.configAuth.userName;
      })
    );
  }

  public async authenticateUserNameAsync(mandator: string, userName: string): Promise<any> {
    return (await this.authenticateUserName$(mandator, userName).toPromise());
  }

  public authenticateUser$(mandator: string, userName: string, password: string): Observable<IDtoUser> {
    const body: any = {
      authenticationUserName: this.configurationService.configAuth.authUserName,
      authenticationPassword: this.configurationService.configAuth.authPassword,
      mandator: UtilsString.encodeBase64(mandator),
      userName: UtilsString.encodeBase64(userName),
      password: UtilsString.encodeBase64(password)
    }
    return this.httpPost('/api/web/authenticate/user', body, 3000, 0).pipe(
      map((json: any) => {
        let result: IDtoUser = null;
        if (UtilsMisc.isNullOrUndefined(json.user) === false) {
          result = (UtilsJson.deserialize(json.user, DtoUser) as IDtoUser);
          if (result !== null) {
            result.setReferences();
            this.configurationService.configAuth.userName = result.userName;
          }
        }

        return result;
      })
    );
  }

  public async authenticateUserAsync(mandator: string, userName: string, password: string): Promise<IDtoUser> {
    return (await this.authenticateUser$(mandator, userName, password).toPromise());
  }

  public resetPasswordForUser$(mandator: string, userName: string, password: string): Observable<IDtoUser> {
    const body: any = {
      authenticationUserName: this.configurationService.configAuth.authUserName,
      authenticationPassword: this.configurationService.configAuth.authPassword,
      mandator: UtilsString.encodeBase64(mandator),
      userName: UtilsString.encodeBase64(userName),
      password: UtilsString.encodeBase64(password)
    }
    return this.httpPost('/api/web/authenticate/user/password', body, 3000, 0).pipe(
      map((json: any) => {
        let result: IDtoUser = null;
        if (UtilsMisc.isNullOrUndefined(json.user) === false) {
          result = (UtilsJson.deserialize(json.user, DtoUser) as IDtoUser);
          if (result !== null) {
            result.setReferences();
            this.configurationService.configAuth.userName = result.userName;
          }
        }

        return result;
      })
    );
  }

  public async resetPasswordForUserAsync(mandator: string, userName: string, password: string): Promise<IDtoUser> {
    return (await this.resetPasswordForUser$(mandator, userName, password).toPromise());
  }

  protected createUserEntityFromJson(json: any): IDtoUser {
    const result: IDtoUser = (UtilsJson.deserialize(json, DtoUser) as IDtoUser);
    if (result !== null) {
      result.setReferences();
    }

    return result;
  }

  protected handleHttpError(error: Error): Observable<never> {
    let result: Error = error;
    if (error instanceof TimeoutError) {
      result = new ErrorTimeout();
    } else if (error instanceof HttpErrorResponse) {
      const httpError: HttpErrorResponse = (error as HttpErrorResponse);
      result = new ErrorHttp(httpError.status, httpError.statusText, ((UtilsMisc.isNullOrUndefined(httpError.error) === false) ? httpError.error.errorMessage : httpError.statusText));
      switch (httpError.status) {
        case 0: {
          result.message = 'Es konnte keine Verbindung zum Server aufgebaut werden.';
          break;
        }
        case 403: {
          result.message = 'Keine Berechtigung für Serveraufrufe.';
          break;
        }
      }
    } else {
      result = new ErrorUnknown(error.name, error.message);
    }
    this.loggingService.error(result);
    return throwError(result);
  }

  protected httpPost(url: string, body: any, timeoutInMilliSeconds: number = null, maxRetries: number = null, retryDelay: number = null): Observable<any> {
    if (UtilsMisc.isNullOrUndefined(timeoutInMilliSeconds) === true) {
      timeoutInMilliSeconds = this.configurationService.configData.timeout;
    }
    if (UtilsMisc.isNullOrUndefined(maxRetries) === true) {
      maxRetries = this.configurationService.configData.retries;
    }
    if (UtilsMisc.isNullOrUndefined(retryDelay) === true) {
      retryDelay = this.configurationService.configData.retryDelay;
    }
    return this.http.post(url, body).pipe(
      timeout(timeoutInMilliSeconds),
      catchError(this.handleHttpError.bind(this)),
      retryWithDelay(retryDelay, maxRetries)
    );
  }

  protected getUrl(url: string): string {
    return UtilsString.stringFormat('{0}://{1}:{2}/{3}/authenticate/{4}', this.configurationService.configData.protocol, 'localhost', '55000', this.configurationService.configData.urlApi, url);
  }

}
